import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import Avatar from '../common/Avatar';
import useAuth from '../../hooks/useAuth';
import { createComment, getPostComments } from '../../api/comments';

/**
 * CommentForm component for creating new comments
 * @param {Object} props - Component props
 * @param {string} props.postId - ID of the post to comment on
 * @param {string} props.parentId - ID of the parent comment (for replies)
 * @param {Function} props.onSuccess - Callback for successful comment creation
 * @param {Function} props.onCancel - Callback for form cancellation
 * @param {string} props.placeholder - Placeholder text for the input
 * @returns {JSX.Element} CommentForm component
 */
const CommentForm = ({
  postId,
  parentId,
  onSuccess,
  onCancel,
  placeholder = 'Add a comment...',
}) => {
  const { user, isAuth } = useAuth();
  const [content, setContent] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mediaInputRef = useRef(null);
  
  // Handle content change
  const handleContentChange = (e) => {
    setContent(e.target.value);
  };
  
  // Handle media selection
  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload an image or video.');
      return;
    }
    
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is ${maxSize / (1024 * 1024)}MB.`);
      return;
    }
    
    setMedia(file);
    setMediaPreview(URL.createObjectURL(file));
    setError('');
  };
  
  // Remove media
  const handleRemoveMedia = () => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    
    setMedia(null);
    setMediaPreview(null);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuth) return;
    
    if (!content.trim() && !media) {
      setError('Please enter a comment or add media');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError('');
      
      const commentData = {
        content: content.trim(),
        postId,
        media,
      };
      
      if (parentId) {
        commentData.parentId = parentId;
      }
      
      // Create the comment
      await createComment(commentData);
      
      // Clear form
      setContent('');
      handleRemoveMedia();
      
      // Fetch the updated comments list to ensure we have the latest data with proper structure
      try {
        const response = await getPostComments(postId, { page: 1 });
        
        if (response?.data?.data?.comments && onSuccess) {
          // Pass the updated comments to the parent component
          onSuccess(response.data.data.comments[0]); // Most recent comment should be first
        }
      } catch (fetchError) {
        console.error('Error fetching updated comments:', fetchError);
        // Even if fetching fails, we know the comment was created
        // We'll inform the user that a reload might be needed
        setError('Comment was posted but couldn\'t refresh the comments list automatically.');
      }
    } catch (error) {
      console.error('Error creating comment:', error);
      setError(error.response?.data?.message || 'Failed to create comment');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isAuth) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg text-center">
        <p className="text-gray-600">
          Please{' '}
          <a href="/login" className="text-blue-600 hover:underline">
            sign in
          </a>{' '}
          to join the conversation.
        </p>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="flex">
        <Avatar
          src={user?.profilePicture}
          alt={user?.name}
          size="sm"
          isOnline={user?.isOnline}
          className="mr-3 flex-shrink-0"
        />
        
        <div className="flex-1">
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
            <textarea
              className="w-full p-3 border-none focus:outline-none resize-none"
              placeholder={placeholder}
              rows={2}
              value={content}
              onChange={handleContentChange}
            ></textarea>
            
            {/* Media preview */}
            {mediaPreview && (
              <div className="p-3 pt-0 relative">
                <div className="relative w-24 h-24 overflow-hidden rounded border border-gray-200">
                  {media.type.startsWith('image/') ? (
                    <img
                      src={mediaPreview}
                      alt="Media preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      src={mediaPreview}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                    onClick={handleRemoveMedia}
                  >
                    <svg
                      className="h-3 w-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {/* Form actions */}
            <div className="flex items-center justify-between p-2 border-t border-gray-200 bg-gray-50">
              <div>
                {/* Media upload button */}
                <button
                  type="button"
                  className="p-1 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                  onClick={() => mediaInputRef.current.click()}
                >
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                <input
                  type="file"
                  ref={mediaInputRef}
                  className="hidden"
                  onChange={handleMediaSelect}
                  accept="image/jpeg,image/png,image/gif,video/mp4"
                />
              </div>
              
              <div className="flex space-x-2">
                {/* Cancel button (for replies) */}
                {onCancel && (
                  <Button
                    type="button"
                    variant="light"
                    size="sm"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
                
                {/* Submit button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || (!content.trim() && !media)}
                >
                  {parentId ? 'Reply' : 'Comment'}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Error message */}
          {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        </div>
      </div>
    </form>
  );
};

CommentForm.propTypes = {
  postId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  placeholder: PropTypes.string,
};

export default CommentForm;